import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton } from 'sonata-react-components';
import { isNil } from 'taptap-tools/utils';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import OtaSummary from './OtaSummary';
import ReportLogo from './ReportLogo';

import type {
  DoohReportAudience,
  DoohReportLineItem,
  DoohReportMetrics
} from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';
import type { DoohAudienceProfilingSummary } from 'sonata-static-storage/lib/schemas/doohReport/doohAudienceProfiling.schema';

import './reportSummaryCard.scss';

type Props = {
  lineItem: DoohReportLineItem;
  metrics: DoohReportMetrics;
  audience: DoohReportAudience;
  isSummaryLoading: boolean;
  summary: DoohAudienceProfilingSummary | null;
};

/** Beyond this, the campaign line is elided and the full set moves to its title. */
const MAX_VISIBLE_CARTOGRAPHIES = 2;

/**
 * Splits a formatted value into its numeric portion and any trailing symbols.
 * Returns `N/A` as the value when the input is null or undefined.
 *
 * @param value - The formatted string or number to split.
 * @returns The numeric portion and, when present, its non-numeric symbol.
 */
function extractCardData(value?: string | number | null): { value: string; symbol?: string | null } {
  if (isNil(value)) {
    return { value: 'N/A' };
  }

  return value
    .toString()
    .split('')
    .reduce(
      (acc, char) => {
        if (!isNaN(Number(char)) || [',', '.'].includes(char)) {
          acc.value += char.replace(' ', '');
        } else {
          acc.symbol += char.replace(' ', '');
        }
        return acc;
      },
      { symbol: '', value: '' }
    );
}

/**
 * The campaign's on-target share as "N in 10 impacts", rounded to the nearest
 * impact (62% -> 6, 68.23% -> 7). `onTargetPct` can exceed 100 (it is measured
 * against the target universe), so the result is capped at 10.
 */
function getImpactsOutOfTen(onTargetPct: number | null): number | null {
  return onTargetPct === null ? null : Math.min(10, Math.max(0, Math.round(onTargetPct / 10)));
}

const MINI_CARD_ICONS = {
  budget: {
    className: 'fa-solid fa-piggy-bank',
    color: 'var(--color-status-warning-600, #FF6B08)',
    containerStyle: { background: 'linear-gradient(135deg, #FFEDD4 0%, #FFD6A8 100%)' }
  },
  impressions: {
    className: 'fa-regular fa-eye',
    color: 'var(--color-ai-primary-700, #1909C6)',
    containerStyle: { background: 'linear-gradient(135deg, #DBE3FF 0%, #CBD6FF 100%)' }
  },
  playouts: {
    className: 'fa-kit fa-screen-play',
    color: 'var(--color-tesela-interaction-600, #503CA1)',
    containerStyle: { background: 'linear-gradient(135deg, #EEE9FF 0%, #D3C9FF 100%)' }
  },
  totalScreens: {
    className: 'fa-kit fa-screen',
    color: 'var(--color-extras-500, #1E7BEA)',
    containerStyle: { background: 'linear-gradient(135deg, #EAF4FF 0%, #C9E2FF 100%)' }
  }
} as const;

/**
 * Top summary card of the DOOH report (Reporting V2): the line item's media and
 * AI-written summary on the left, the campaign OTA panel and the four headline
 * metrics on the right.
 */
export default function ReportSummaryCard({ lineItem, metrics, audience, isSummaryLoading, summary }: Readonly<Props>) {
  const { formatMessage, formatDate } = useIntl();
  const { formatAbbreviated, formatCount, formatMoney } = useDoohReportFormatters();

  const miniCards = [
    {
      icon: MINI_CARD_ICONS.budget,
      key: 'budget',
      subtitle: formatMessage({ defaultMessage: 'Total spent', id: 'doohReport.summaryCard.budget' }),
      ...extractCardData(formatMoney(metrics.budget))
    },
    {
      icon: MINI_CARD_ICONS.totalScreens,
      key: 'totalScreens',
      subtitle: formatMessage({ defaultMessage: 'Total screens', id: 'doohReport.summaryCard.totalScreens' }),
      ...extractCardData(formatCount(metrics.totalScreens))
    },
    {
      icon: MINI_CARD_ICONS.playouts,
      key: 'playouts',
      subtitle: formatMessage({ defaultMessage: 'Total Playouts', id: 'doohReport.summaryCard.playouts' }),
      ...extractCardData(formatCount(metrics.playouts))
    },
    {
      icon: MINI_CARD_ICONS.impressions,
      key: 'impressions',
      subtitle: formatMessage({ defaultMessage: 'Total Impressions', id: 'doohReport.summaryCard.impressions' }),
      ...extractCardData(formatAbbreviated(metrics.impressions))
    }
  ];

  const dateOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const dateRange =
    lineItem.startDate && lineItem.endDate
      ? `${formatDate(new Date(lineItem.startDate), dateOptions)} - ${formatDate(new Date(lineItem.endDate), dateOptions)}`
      : null;
  const cartographies = metrics.universeCartographies;
  const buildInfo = (locationsLabel: string | null): string => [dateRange, locationsLabel].filter(Boolean).join(' · ');
  const summaryLocations = summary?.locations || null;
  // The line item name is elided first, so the dates and locations after it stay visible.
  const campaignDetails = buildInfo(
    summaryLocations ??
      (cartographies.length > MAX_VISIBLE_CARTOGRAPHIES
        ? `${cartographies.slice(0, MAX_VISIBLE_CARTOGRAPHIES).join(' & ')}…`
        : cartographies.join(' & ') || null)
  );
  const campaignDetailsTitle =
    !summaryLocations && cartographies.length > MAX_VISIBLE_CARTOGRAPHIES
      ? buildInfo(cartographies.join(' & '))
      : campaignDetails;
  const campaignInfoTitle = [lineItem.name, campaignDetailsTitle].filter(Boolean).join(' · ');

  const impactsOutOfTen = getImpactsOutOfTen(audience.onTargetPct);
  const targetDescriptor = summary?.target_descriptor ? (
    <span className="reportSummaryCard__insightTarget">{summary.target_descriptor}</span>
  ) : null;
  const buildHeadline = () => {
    if (impactsOutOfTen === null) {
      return targetDescriptor;
    }
    return targetDescriptor
      ? formatMessage(
          { defaultMessage: '{count} in 10 impacts reached {target}', id: 'doohReport.summaryCard.headline' },
          { count: impactsOutOfTen, target: targetDescriptor }
        )
      : formatMessage(
          {
            defaultMessage: '{count} in 10 impacts reached the target audience',
            id: 'doohReport.summaryCard.headlineNoTarget'
          },
          { count: impactsOutOfTen }
        );
  };
  const headline = buildHeadline();

  return (
    <div className="reportSummaryCard">
      <div className="reportSummaryCard__campaign">
        <ReportLogo lineItemId={lineItem.id} />
        <div className="reportSummaryCard__details">
          <HoverTooltip content={campaignInfoTitle}>
            <span className="reportSummaryCard__campaignInfo">
              <span className="reportSummaryCard__campaignName">{lineItem.name}</span>
              {campaignDetails && <span className="reportSummaryCard__campaignDetails">{` · ${campaignDetails}`}</span>}
            </span>
          </HoverTooltip>
          {isSummaryLoading && (
            <div className="reportSummaryCard__insightSkeleton">
              <LoadingSkeleton height={24} width="60%" />
              <LoadingSkeleton height={14} width="95%" />
              <LoadingSkeleton height={14} width="80%" />
            </div>
          )}
          {!isSummaryLoading && summary && (
            <>
              {headline && <h3 className="reportSummaryCard__insightTitle">{headline}</h3>}
              <p className="reportSummaryCard__insightDescription">{summary.description}</p>
            </>
          )}
        </div>
      </div>

      <div className="reportSummaryCard__metrics">
        <OtaSummary ota={metrics.ota} />
        <div className="reportSummaryCard__miniCards">
          {miniCards.map((card, index) => (
            <Fragment key={card.key}>
              {index > 0 && <div className="reportSummaryCard__miniCardDivider" />}
              <div className="reportSummaryCard__miniCard">
                <div className="reportSummaryCard__miniCardIcon" style={card.icon.containerStyle}>
                  <i className={card.icon.className} style={{ color: card.icon.color }} />
                </div>
                <span className="reportSummaryCard__miniCardValue">
                  {card.value}
                  <span className="reportSummaryCard__miniCardValueSymbol">{card.symbol}</span>
                </span>
                <span className="reportSummaryCard__miniCardSubtitle">{card.subtitle}</span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
